/* Shared lifecycle and opt-in media for the unpublished Pinball FX review templates. */
(() => {
  'use strict';
  if (window.ZXReview) return;
  function lifecycle(root) {
    const abort = new AbortController(), cleanups = [], timers = new Set(), intervals = new Set(), frames = new Set();
    let disposed = false;
    const api = {
      get disposed() { return disposed; },
      on(target, type, fn, options = {}) { if (!target || disposed) return; target.addEventListener(type, fn, { ...(typeof options === 'boolean' ? { capture: options } : options), signal: abort.signal }); },
      cleanup(fn) { cleanups.push(fn); },
      timeout(fn, ms) { if (disposed) return 0; const id = window.setTimeout(() => { timers.delete(id); if (!disposed) fn(); }, ms); timers.add(id); return id; },
      clearTimeout(id) { window.clearTimeout(id); timers.delete(id); },
      interval(fn, ms) { if (disposed) return 0; const id = window.setInterval(() => { if (!disposed) fn(); }, ms); intervals.add(id); return id; },
      clearInterval(id) { window.clearInterval(id); intervals.delete(id); },
      raf(fn) { if (disposed) return 0; const id = window.requestAnimationFrame(t => { frames.delete(id); if (!disposed) fn(t); }); frames.add(id); return id; },
      cancelRaf(id) { window.cancelAnimationFrame(id); frames.delete(id); },
      observe(kind, fn, options) { const observer = new window[kind](fn, options); cleanups.push(() => observer.disconnect()); return observer; },
      dispose() { if (disposed) return; disposed = true; abort.abort(); timers.forEach(window.clearTimeout); intervals.forEach(window.clearInterval); frames.forEach(window.cancelAnimationFrame); cleanups.splice(0).reverse().forEach(fn => fn()); }
    };
    api.on(document, 'shopify:section:unload', event => { if (event.target === root || event.target.contains(root)) api.dispose(); });
    api.on(window, 'pagehide', event => { if (!event.persisted) api.dispose(); });
    return api;
  }
  // All animated sections measure first, then mutate in one shared frame.
  const pendingFrames = new Map();
  let animationFrame = 0;
  function flushFrames() {
    animationFrame = 0;
    const work = [...pendingFrames.values()]; pendingFrames.clear();
    const measured = work.map(task => task.life.disposed ? null : task.measure());
    work.forEach((task, index) => { if (!task.life.disposed && measured[index] !== null) task.mutate(measured[index]); });
  }
  function frameTask(life, measure, mutate) {
    const task = { life, measure, mutate };
    const cancel = () => {
      pendingFrames.delete(task);
      if (!pendingFrames.size && animationFrame) { window.cancelAnimationFrame(animationFrame); animationFrame = 0; }
    };
    life.cleanup(cancel);
    return {
      schedule() {
        if (life.disposed || document.hidden) return;
        pendingFrames.set(task, task);
        if (!animationFrame) animationFrame = window.requestAnimationFrame(flushFrames);
      }, cancel
    };
  }
  const instances = new Map();
  let viewerPromise;
  const loadViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    return viewerPromise || (viewerPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = new URL(window.PinballFXV15.assets["assets/model-viewer-classic.min.js"], document.baseURI).href;
      script.onload = () => customElements.whenDefined('model-viewer').then(resolve);
      script.onerror = () => { viewerPromise = null; script.remove(); reject(new Error('3D viewer unavailable')); };
      document.head.append(script);
    }));
  };

  const loadModelData = () => Promise.resolve({ acquire: () => window.PinballFXV15.modelUrl, release() {} });

  // aria-hidden removes decorative media from assistive technology; it does not hide pixels.
  const isExposed = el => !el.closest('[hidden],[inert]');
  function init(root) {
    if (instances.has(root)) return;
    const life = lifecycle(root), motion = matchMedia('(prefers-reduced-motion: reduce)'), models = new Map(), reflections = new Set();
    instances.set(root, life); life.cleanup(() => instances.delete(root));
    const animatedSections = [...root.querySelectorAll(':scope > section, :scope > .zx-wrap > section')];
    const animationObserver = life.observe('IntersectionObserver', entries => {
      entries.forEach(entry => entry.target.classList.toggle('zx-perf-offscreen', !entry.isIntersecting));
    });
    animatedSections.forEach(section => { section.classList.add('zx-perf-offscreen'); animationObserver.observe(section); });
    const hiddenState = () => root.classList.toggle('zx-perf-hidden', document.hidden);
    hiddenState(); life.on(document, 'visibilitychange', hiddenState);
    life.cleanup(() => { root.classList.remove('zx-perf-hidden'); animatedSections.forEach(section => section.classList.remove('zx-perf-offscreen')); });
    root.querySelectorAll('model-viewer[data-review-model-src]').forEach(model => {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'zx-btn review-load-model';
      button.textContent = 'Load interactive 3D model'; model.before(button);
      const state = { visible: false, requested: false, userRotate: !motion.matches, loading: null };
      models.set(model, state);
      const reflect = () => { const rotate = !!(state.requested && state.userRotate && state.visible && isExposed(model) && !document.hidden && !motion.matches); if (model.hasAttribute('auto-rotate') !== rotate) model.toggleAttribute('auto-rotate', rotate); };
      reflections.add(reflect);
      const load = () => {
        if (state.loading) return state.loading;
        button.disabled = true; button.textContent = 'Loading 3D viewer…';
        state.loading = Promise.all([loadViewer(), loadModelData()]).then(([, modelData]) => {
          if (life.disposed) return;
          state.modelData = modelData; state.modelURL = modelData.acquire();
          state.requested = true; model.setAttribute('src', state.modelURL); button.remove(); reflect();
        }).catch(() => { state.loading = null; if (!life.disposed) { button.disabled = false; button.textContent = 'Retry loading 3D model'; } });
        return state.loading;
      };
      model.zxLoadModel = load;
      model.zxSetRotation = on => { state.userRotate = !!on; reflect(); };
      life.on(button, 'click', load);
      life.observe('IntersectionObserver', entries => { state.visible = entries[0].isIntersecting; reflect(); }, { threshold: .1 }).observe(model);
      life.on(document, 'visibilitychange', reflect); life.on(motion, 'change', reflect); life.on(root, 'zx:media-change', reflect);

      life.cleanup(() => { model.removeAttribute('auto-rotate'); model.removeAttribute('src'); if (state.modelData && state.modelURL) { state.modelData.release(); state.modelURL = null; } button.remove(); delete model.zxLoadModel; delete model.zxSetRotation; });
      reflect();
    });
    // One observer per section, limited to actual visibility changes.
    if (reflections.size) life.observe('MutationObserver', () => reflections.forEach(reflect => reflect())).observe(root, { subtree: true, attributes: true, attributeFilter: ['hidden', 'inert'] });
    // Playback owners keep their manual-pause state. This guard only enforces visibility.
    root.querySelectorAll('video:not([data-local-scroll-play])').forEach(video => {
      let visible = false, resume = false, internalPause = false;
      const pause = () => { if (!video.paused) { resume = true; internalPause = true; video.pause(); internalPause = false; } };
      const reflect = () => {
        if (!visible || !isExposed(video) || document.hidden) pause();
        else if (resume && !motion.matches && video.dataset.zxUserPaused !== 'true') { resume = false; video.play().catch(() => {}); }
      };
      life.observe('IntersectionObserver', entries => { visible = entries[0].isIntersecting; reflect(); }, { threshold: .05 }).observe(video);
      life.on(video, 'play', () => { if (!visible || document.hidden || !isExposed(video) || video.dataset.zxUserPaused === 'true') pause(); });
      life.on(video, 'pause', () => { if (!internalPause && video.dataset.zxUserPaused === 'true') resume = false; });
      life.on(document, 'visibilitychange', reflect); life.on(root, 'zx:media-change', reflect);
      life.on(motion, 'change', () => { if (motion.matches) { resume = false; video.pause(); } });
      life.cleanup(() => video.pause());
    });
  }
  function boot(scope = document) { if (scope.matches?.('[data-zx-root]')) init(scope); scope.querySelectorAll('[data-zx-root]').forEach(init); }
  window.ZXReview = { lifecycle, frameTask, loadViewer, isExposed };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot()); else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
